"use client";

// app/global-error.tsx
// Root-layout error boundary — catches errors thrown in app/layout.tsx
// itself, which app/error.tsx cannot (it's nested inside the layout).
// Replaces the entire document when triggered, so it renders its own
// <html>/<body> rather than relying on the root layout.

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import "./globals.css";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[GlobalError:root-layout]", error);
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="en">
      <body className="font-sans min-h-screen bg-content-bg text-content-text">
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <p className="text-5xl font-bold text-forge-urgency mb-4 font-mono">
              Error
            </p>
            <h1 className="text-xl font-semibold text-content-text mb-3">
              Something went wrong
            </h1>
            <p className="text-sm text-content-muted mb-8 leading-relaxed">
              An unexpected error occurred. Please try again or return to the
              homepage.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={reset}
                className="rounded-lg border border-content-border px-6 py-3 text-sm font-medium text-content-text transition-colors hover:border-content-muted"
              >
                Try Again
              </button>
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
              <a
                href="/"
                className="rounded-lg bg-forge-trust px-6 py-3 text-sm font-semibold text-content-bg transition-colors hover:bg-forge-trust-hover"
              >
                Go Home
              </a>
            </div>
            {error.digest && (
              <p className="mt-6 text-xs text-content-muted font-mono">
                Error ID: {error.digest}
              </p>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}
